import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { uploadMarkdownImage } from '../../../api/markdownImages';
import { searchTicketsByPrefix } from '../../../api/ticketReferences';
import { useToast } from '../../common/Toast';
import Avatar from '../../common/Avatar';
import './MentionInput.scss';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB (與 bucket file_size_limit 一致)

const STATUS_COLORS = {
  open: '#94a3b8',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  closed: '#6b7280',
};

// enableTicketRef:打 # 跳 Ticket 自動完成。預設開(舊 Ticket 沿用),工作項目傳 false ——
// 那邊的關聯改成在 Drawer 右側手動設定,不再從內文的 #編號 推關聯。
// onFileUpload:有給才收「非圖片」檔案(拖曳 / 貼上)。async (file) => { markdown } | { error }。
// 沒給 = 維持原本只收圖片的行為,舊 Ticket 那邊不受影響。
// 圖片走原本的 uploadMarkdownImage(item-images public bucket)不動,非圖片才走呼叫端給的上傳。
const MentionInput = ({ value, onChange, onKeyDown, placeholder, departmentId, companyId, enableTicketRef = true, onFileUpload = null }) => {
  const { companyUsers: users } = useBetaData();
  const toast = useToast();
  const textareaRef = useRef(null);
  // 一次拖多個檔案時會連續 await,期間 value prop 不會更新 → 自己追最新值,避免後面蓋掉前面
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);
  const suggestionsRef = useRef(null);
  const ticketSuggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // @ mention state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // # ticket state
  const [showTicketSuggestions, setShowTicketSuggestions] = useState(false);
  const [ticketQuery, setTicketQuery] = useState('');
  const [ticketStartIndex, setTicketStartIndex] = useState(-1);
  const [selectedTicketIndex, setSelectedTicketIndex] = useState(0);
  const [ticketSuggestions, setTicketSuggestions] = useState([]);

  const [uploading, setUploading] = useState(false);
  // 上傳進度:{ name, index, total }。序列上傳多檔時要看得出跑到第幾個、正在傳誰,
  // 否則只有一句「上傳中」,使用者會以為卡住而重複拖曳。
  const [uploadInfo, setUploadInfo] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const filteredUsers = useMemo(
    () =>
      users
        .filter((u) => u.name?.toLowerCase().includes(suggestionQuery.toLowerCase()))
        .slice(0, 8),
    [users, suggestionQuery]
  );

  // Debounced ticket search
  const searchTickets = useCallback(async (query) => {
    if (!query || !companyId) {
      setTicketSuggestions([]);
      return;
    }
    const results = await searchTicketsByPrefix(query, companyId);
    setTicketSuggestions(results);
  }, [companyId]);

  useEffect(() => {
    if (!showTicketSuggestions || !ticketQuery) {
      setTicketSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchTickets(ticketQuery), 300);
    return () => clearTimeout(debounceRef.current);
  }, [ticketQuery, showTicketSuggestions, searchTickets]);

  const handleInput = (e) => {
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(text);

    const textBeforeCursor = text.slice(0, cursorPos);

    // 從游標往回找最近的 @ 或 #，誰近就用誰
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const lastHashIndex = textBeforeCursor.lastIndexOf('#');

    // 決定優先順序：離游標最近的觸發字元優先
    const triggers = [];
    if (lastAtIndex !== -1) triggers.push({ type: 'mention', index: lastAtIndex });
    if (enableTicketRef && lastHashIndex !== -1) triggers.push({ type: 'ticket', index: lastHashIndex });
    triggers.sort((a, b) => b.index - a.index); // 最近的排前面

    for (const trigger of triggers) {
      const query = textBeforeCursor.slice(trigger.index + 1);
      if (query.includes(' ') || query.includes(']')) continue;

      if (trigger.type === 'mention') {
        setSuggestionQuery(query);
        setMentionStartIndex(trigger.index);
        setShowSuggestions(true);
        setSelectedIndex(0);
        setShowTicketSuggestions(false);
        return;
      } else {
        setTicketQuery(query);
        setTicketStartIndex(trigger.index);
        setShowTicketSuggestions(true);
        setSelectedTicketIndex(0);
        setShowSuggestions(false);
        return;
      }
    }

    setShowSuggestions(false);
    setShowTicketSuggestions(false);
  };

  const handleSelectUser = (user) => {
    const before = value.slice(0, mentionStartIndex);
    const after = value.slice(textareaRef.current.selectionStart);
    const mention = `@[${user.name}](${user.id})`;
    const newValue = `${before}${mention} ${after}`;
    onChange(newValue);
    setShowSuggestions(false);

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + mention.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleSelectTicket = (ticket) => {
    const before = value.slice(0, ticketStartIndex);
    const after = value.slice(textareaRef.current.selectionStart);
    const ref = `#${ticket.ticket_no}`;
    const newValue = `${before}${ref} ${after}`;
    onChange(newValue);
    setShowTicketSuggestions(false);

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + ref.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDownInternal = (e) => {
    // @ mention keyboard navigation
    if (showSuggestions && filteredUsers.length > 0) {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const nextDown = (selectedIndex + 1) % filteredUsers.length;
          setSelectedIndex(nextDown);
          suggestionsRef.current?.children[nextDown]?.scrollIntoView({ block: 'nearest' });
          return;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const nextUp = (selectedIndex - 1 + filteredUsers.length) % filteredUsers.length;
          setSelectedIndex(nextUp);
          suggestionsRef.current?.children[nextUp]?.scrollIntoView({ block: 'nearest' });
          return;
        }
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          handleSelectUser(filteredUsers[selectedIndex]);
          return;
        case 'Escape':
          setShowSuggestions(false);
          e.stopPropagation();
          return;
        default:
          break;
      }
    }

    // # ticket keyboard navigation
    if (showTicketSuggestions && ticketSuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const nextDown = (selectedTicketIndex + 1) % ticketSuggestions.length;
          setSelectedTicketIndex(nextDown);
          ticketSuggestionsRef.current?.children[nextDown]?.scrollIntoView({ block: 'nearest' });
          return;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const nextUp = (selectedTicketIndex - 1 + ticketSuggestions.length) % ticketSuggestions.length;
          setSelectedTicketIndex(nextUp);
          ticketSuggestionsRef.current?.children[nextUp]?.scrollIntoView({ block: 'nearest' });
          return;
        }
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          handleSelectTicket(ticketSuggestions[selectedTicketIndex]);
          return;
        case 'Escape':
          setShowTicketSuggestions(false);
          e.stopPropagation();
          return;
        default:
          break;
      }
    }

    onKeyDown?.(e);
  };

  // 在游標處插入一段 markdown,並把游標移到後面
  const insertAtCursor = (markdown) => {
    const current = valueRef.current ?? '';
    const textarea = textareaRef.current;
    const cursorPos = textarea?.selectionStart ?? current.length;
    const newValue = current.slice(0, cursorPos) + markdown + '\n' + current.slice(cursorPos);
    valueRef.current = newValue;
    onChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = cursorPos + markdown.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // 非圖片檔案:上傳交給呼叫端(工作項目留言 → work_item_attachments),插入它回傳的 markdown
  const insertFile = async (file) => {
    if (!onFileUpload) return;
    setUploading(true);
    try {
      const result = await onFileUpload(file);
      if (result?.markdown) insertAtCursor(result.markdown);
      else toast.error(result?.error || `「${file.name}」上傳失敗`);
    } catch (err) {
      toast.error(`「${file.name}」上傳失敗`);
    } finally {
      setUploading(false);
    }
  };

  const insertImage = async (imageFile) => {
    if (imageFile.size > MAX_IMAGE_SIZE) {
      toast.error('圖片大小不能超過 10MB');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadMarkdownImage(imageFile, departmentId);
      if (result.success && result.url) {
        insertAtCursor(`![image](${result.url})`);
      } else {
        const msg = result.error?.includes('maximum allowed size')
          ? '圖片檔案過大，超過上傳限制'
          : (result.error || '圖片上傳失敗');
        toast.error(msg);
      }
    } catch (err) {
      toast.error('圖片上傳失敗：' + (err.message || '未知錯誤'));
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageItem = [...items].find((i) => i.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const f = imageItem.getAsFile();
      setUploadInfo({ name: f?.name || '圖片', index: 1, total: 1 });
      insertImage(f).finally(() => setUploadInfo(null));
      return;
    }
    if (!onFileUpload) return;
    const fileItem = [...items].find((i) => i.kind === 'file');
    const file = fileItem?.getAsFile();
    if (!file) return;
    e.preventDefault();
    setUploadInfo({ name: file.name, index: 1, total: 1 });
    insertFile(file).finally(() => setUploadInfo(null));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragging(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;

    if (!onFileUpload) {
      // 沒開放檔案上傳(舊 Ticket)→ 維持原本只收第一張圖片
      const imageFile = files.find((f) => f.type.startsWith('image/'));
      if (imageFile) {
        setUploadInfo({ name: imageFile.name, index: 1, total: 1 });
        insertImage(imageFile).finally(() => setUploadInfo(null));
      }
      return;
    }
    // 逐一處理,不要並行 —— 插入位置要接續,並行會互相蓋掉
    (async () => {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadInfo({ name: f.name, index: i + 1, total: files.length });
        if (f.type.startsWith('image/')) await insertImage(f);
        else await insertFile(f);
      }
      setUploadInfo(null);
    })();
  };

  return (
    <div
      className={`mention-input${dragging ? ' mention-input--dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDownInternal}
        onPaste={handlePaste}
        placeholder={placeholder}
        rows={3}
        className={uploading ? 'uploading' : ''}
      />

      {dragging && (
        <div className="mention-input-drop-overlay">
          <span>{onFileUpload ? '放開以上傳檔案' : '放開以上傳圖片'}</span>
        </div>
      )}

      {uploading && (
        <div className="mention-input-status">
          <span className="mention-input-spinner" />
          {uploadInfo
            ? `上傳中 ${uploadInfo.name}${uploadInfo.total > 1 ? ` (${uploadInfo.index}/${uploadInfo.total})` : ''}`
            : '上傳中...'}
        </div>
      )}

      {/* @ mention suggestions */}
      {showSuggestions && filteredUsers.length > 0 && (
        <div className="mention-suggestions" ref={suggestionsRef}>
          {filteredUsers.map((user, index) => (
            <button
              key={user.id}
              className={`suggestion-item ${index === selectedIndex ? 'suggestion-item--selected' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectUser(user);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <Avatar
                avatarUrl={user.avatar_url}
                name={user.name}
                size="tiny"
              />
              <span className="suggestion-name">{user.name}</span>
              {user.department_name && (
                <span className="suggestion-dept">{user.department_name}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* # ticket suggestions */}
      {showTicketSuggestions && ticketSuggestions.length > 0 && (
        <div className="ticket-suggestions" ref={ticketSuggestionsRef}>
          {ticketSuggestions.map((ticket, index) => (
            <button
              key={ticket.id}
              className={`suggestion-item ${index === selectedTicketIndex ? 'suggestion-item--selected' : ''} ${ticket.status === 'closed' ? 'suggestion-item--closed' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectTicket(ticket);
              }}
              onMouseEnter={() => setSelectedTicketIndex(index)}
            >
              <span className="suggestion-status-dot" style={{ backgroundColor: STATUS_COLORS[ticket.status] || '#94a3b8' }} />
              <span className="suggestion-ticket-no">#{ticket.ticket_no}</span>
              <span className="suggestion-ticket-title">{ticket.title}</span>
              {ticket.projects?.name && (
                <span className="suggestion-project">{ticket.projects.name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
